import type { PostHog } from "posthog-js";

/**
 * Ask at the aha, not on page view (2026-10-30).
 *
 * Evidence: scripts/research/anon-vs-authed-2026-10/README.md §3 and lever 2.
 * Anonymous humans whose first session had an apply click signed in later
 * 38.8% of the time and came back within 7 days 14.9% of the time; after a
 * Hunt chat turn, 55.2% / 24.1%; with none of those, 3.1% / 5.8%. The
 * page-view alert capture goes with a 4.0% return, below baseline.
 *
 * `treatment`: right after an anonymous visitor's first external apply click
 * (job detail, `JobIntentSignup`; or a /jobs feed card, `FeedAhaSignupAsk`,
 * trigger `feed_apply`, added 2026-11-01 because 4 of 10 proven-human
 * anonymous apply-clickers applied only from the feed) or first completed
 * Hunt chat turn (`HuntAhaSignupAsk`), show a dismissible "save this + get
 * similar jobs"
 * signup card. Apply is never blocked or delayed: the card listens for the
 * apply event the click handler dispatches AFTER the employer tab has opened.
 *
 * `control`, an unresolved read and any unknown value render today's UI and
 * fire no `experiment_exposure`, so a missing flag is never counted as an arm.
 *
 * PostHog flag "aha-signup-ask" (multivariate control/treatment, 50/50) is the
 * SOLE switch. It supersedes `job-intent-signup` (flag 950183), which shipped
 * the apply-side card on 2026-10-11 but stayed at 0% rollout with zero
 * exposures; that card now reads this flag. Read client-side only, at the
 * trigger, on the anonymous posthog-js distinct_id: the job page is ISR/edge
 * cached and must stay per-user-free.
 */
export const AHA_SIGNUP_ASK_FLAG = "aha-signup-ask";

/** Bounded wait for a late `/decide`, measured from the trigger. */
export const AHA_SIGNUP_ASK_RESOLVE_TIMEOUT_MS = 1_500;

export const AHA_SIGNUP_ASK_SHOWN_EVENT = "aha_signup_ask_shown";
export const AHA_SIGNUP_ASK_CLICKED_EVENT = "aha_signup_ask_clicked";

export type AhaSignupAskArm = "control" | "treatment";
export type AhaSignupAskTrigger = "apply" | "hunt" | "feed_apply";

/**
 * Only the exact strings "control" / "treatment" are arms. `undefined` (not
 * loaded), `false` (off / no match), `true` and unknown strings return null.
 */
export function resolveAhaSignupAskArm(raw: unknown): AhaSignupAskArm | null {
	return raw === "control" || raw === "treatment" ? raw : null;
}

/**
 * Non-production e2e override. Same `ld_preview_force` cookie and
 * `flagKey=variant` syntax as `src/lib/flags/preview-force.ts`, read on the
 * client because this flag is client-only. Two gates, neither of which
 * production can meet: posthog-js must NOT be loaded (production always
 * initialises it; every e2e lane runs with a blank key or on localhost), and
 * the host must not be lokerdollar.com or a subdomain. It can only pick an
 * arm for the browser that set the cookie, never for anyone else.
 */
export function readAhaSignupAskE2eOverride(
	posthogLoaded: boolean,
): AhaSignupAskArm | null {
	if (posthogLoaded) return null;
	try {
		const host = window.location.hostname;
		if (host === "lokerdollar.com" || host.endsWith(".lokerdollar.com")) {
			return null;
		}
		const raw = document.cookie
			.split(";")
			.map((c) => c.trim())
			.find((c) => c.startsWith("ld_preview_force="));
		if (!raw) return null;
		const tokens = decodeURIComponent(raw.slice("ld_preview_force=".length))
			.split(",")
			.map((t) => t.trim());
		for (const token of tokens) {
			const sep = token.indexOf("=");
			if (sep <= 0 || token.slice(0, sep) !== AHA_SIGNUP_ASK_FLAG) continue;
			return resolveAhaSignupAskArm(token.slice(sep + 1));
		}
	} catch {
		/* no document / cookie access: no override */
	}
	return null;
}

type FlagReader = Pick<PostHog, "getFeatureFlag" | "onFeatureFlags"> & {
	__loaded?: boolean;
};

/** Synchronous read: the e2e override, else the live flag. */
export function readAhaSignupAskArmNow(
	posthog: FlagReader,
): AhaSignupAskArm | null {
	return (
		readAhaSignupAskE2eOverride(posthog.__loaded === true) ??
		resolveAhaSignupAskArm(posthog.getFeatureFlag(AHA_SIGNUP_ASK_FLAG))
	);
}

/** Resolve the arm, waiting at most `timeoutMs` for flags to load. Never rejects. */
export function readAhaSignupAskArm(
	posthog: FlagReader,
	timeoutMs: number = AHA_SIGNUP_ASK_RESOLVE_TIMEOUT_MS,
): Promise<AhaSignupAskArm | null> {
	return new Promise((resolve) => {
		let settled = false;
		let unsubscribe: (() => void) | undefined;
		let timer: ReturnType<typeof setTimeout> | undefined;
		const settle = (arm: AhaSignupAskArm | null) => {
			if (settled) return;
			settled = true;
			if (timer) clearTimeout(timer);
			unsubscribe?.();
			resolve(arm);
		};
		const read = () => {
			const arm = readAhaSignupAskArmNow(posthog);
			if (arm) settle(arm);
		};
		read();
		if (settled) return;
		timer = setTimeout(() => settle(null), timeoutMs);
		unsubscribe = posthog.onFeatureFlags(read);
		// onFeatureFlags can call back synchronously when flags are already loaded.
		if (settled) unsubscribe();
	});
}

/**
 * Show once per person (per browser: the person is anonymous). One key for
 * every trigger, so a visitor who saw the ask after an apply is not asked
 * again after a Hunt turn or a feed apply. Dismissing counts as seen.
 */
const SEEN_KEY = "ld_aha_signup_ask_seen";

export function hasSeenAhaSignupAsk(): boolean {
	try {
		return window.localStorage.getItem(SEEN_KEY) !== null;
	} catch {
		return false;
	}
}

export function markAhaSignupAskSeen(trigger: AhaSignupAskTrigger): void {
	try {
		window.localStorage.setItem(SEEN_KEY, trigger);
	} catch {
		/* storage blocked: may show again on a later trigger, apply unaffected */
	}
}
